import { type PropsWithChildren, type ReactNode, useEffect } from 'react'

import { Dialog, DialogContent } from '@mui/material'

import css from './index.module.css'

import { addBlur, removeBlur } from './helper'

export interface Props {
    footer?: ReactNode
    fullWidth?: boolean
    maxWidth?: string
    onClose: () => void
    open: boolean
    render?: (nodes: RenderProps) => ReactNode
    title?: string
    titleBg?: string
    titleColor?: string
}

interface RenderProps {
    closeNode: ReactNode
    contentNode: ReactNode
    footerNode: ReactNode
    titleNode: ReactNode
}

export const Modal = ({ children, footer, fullWidth, maxWidth, onClose, open, render, title, titleBg, titleColor }: PropsWithChildren<Props>) => {
    const titleStyle = {
        backgroundColor: titleBg,
        color: titleColor ?? 'var(--gray)',
    }

    // !Scroll disabled by Dialog!
    // close modal and hide blur background
    const handleOnClose = () => {
        removeBlur()
        onClose()
    }

    useEffect(() => {
        // show blur background
        open && addBlur()
        !open && removeBlur()
    }, [open])

    render ??= ({ contentNode, footerNode, titleNode }: RenderProps) => (
        <>
            {titleNode}
            {contentNode}
            {footer !== undefined && footerNode}
        </>
    )

    const closeNode = <div className={css.close} onClick={handleOnClose} />

    maxWidth ??= `${850}px`

    return (
        <Dialog
            aria-labelledby="modal"
            fullWidth={fullWidth}
            onClose={handleOnClose}
            open={open}
            PaperProps={{ sx: theme => ({
                width: 'calc(100% - 2rem)',
                maxWidth,
                maxHeight: 'calc(100% - 1rem)',
                m: 2,
                [theme.breakpoints.down(500)]: {
                    maxHeight: 'calc(100% - .5rem)',
                    m: 2,
                },
            }) }}>
            {render({
                closeNode,
                titleNode: (
                    <h3 className={css.title} style={titleStyle}>
                        {title}
                        <div className={css.close} onClick={handleOnClose} />
                    </h3>
                ),
                footerNode: (
                    <div className={css.footer}>
                        {footer}
                    </div>
                ),
                contentNode: (
                    <DialogContent sx={{ p: 0 }}>
                        {children}
                    </DialogContent>
                ),
            })}
        </Dialog>
    )
}
